class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach(
      (button) => button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateHandler = this.onQuantityUpdate.bind(this);
    document.addEventListener('afterQuantityUpdate', this.quantityUpdateHandler);
  }

  disconnectedCallback() {
    if (this.quantityUpdateHandler) {
      document.removeEventListener('afterQuantityUpdate', this.quantityUpdateHandler);
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  onQuantityUpdate() {
    this.validateQtyRules();
  }

  validateQtyRules() {
    if(this.input.value == '' || this.input.value == null) {
      this.input.value = this.input.min;
    } else if(parseInt(this.input.value) % parseInt(this.input.step) > 0) {
      this.input.stepUp();
    }
    const value = parseInt(this.input.value);
    if(this.input.max && (value > parseInt(this.input.max))) {
      this.input.value = this.input.max;
    }
    const finalValue = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', finalValue <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', finalValue >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary:not(.no-control-menu)').forEach(summary => summary.addEventListener('click', this.onSummaryClick.bind(this)));
    this.querySelectorAll('button:not(.no-control-menu)').forEach(button => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if(event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if(!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary')) : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button') || detailsElement.querySelector(':scope > summary'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if(isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);
      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        this.hasBackButton ? document.body.classList.add('open-submenu-mobile') : '';
        !reducedMotion || reducedMotion.matches ? addTrapFocus() : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
    if(this.dataset.id) {
      document.body.classList.add(`open-drawer-${this.dataset.id}`);
    }
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach(details => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.hasBackButton ? document.body.classList.remove('open-submenu-mobile') : '';
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach(submenu => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);
    if(this.dataset.id) {
      document.body.classList.remove(`open-drawer-${this.dataset.id}`);
    }
  }

  onFocusOut(event) {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement)) this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    this.hasBackButton ? document.body.classList.remove('open-submenu-mobile') : '';
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    closeDetailsInAnimation(detailsElement);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
    this.hasBackButton = true;
  }

  openMenuDrawer(summaryElement) {
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
    this.header = this.header || document.querySelector('.section-header');
    this.headerContent = document.querySelector('.header');
    document.documentElement.style.setProperty('--header-bottom-position', `${parseInt(this.headerContent.getBoundingClientRect().bottom)}px`);
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (event === undefined) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
  }
}

customElements.define('header-drawer', HeaderDrawer);

class BackButtonMenu extends HTMLElement {
  constructor() {
    super();

    this.button = this.querySelector('button');
    this.onButtonClickHandler = this.onButtonClick.bind(this);
    this.button.addEventListener('click', this.onButtonClickHandler);
    this.headerDrawer = document.querySelector('header-drawer');
  }

  disconnectedCallback() {
    this.button.removeEventListener('click', this.onButtonClickHandler);
  }

  onButtonClick() {
    const openingElements = this.headerDrawer.querySelectorAll('.menu-opening');
    if(openingElements.length == 1) {
      this.headerDrawer.querySelector('.header__icon').dispatchEvent(new Event('click'));
    } else {
      const lastElement = openingElements[openingElements.length - 1];
      lastElement.querySelector(':scope > div > div > .menu-drawer__close-button').dispatchEvent(new Event('click'));
    }
  }
}

customElements.define('back-button-menu', BackButtonMenu);

const animateResizeObserver = new ResizeObserver((entries, observer) => {
  if(window.innerWidth >= 750) {
    entries.forEach(entry => {
      if(!entry.target.inited) {
        entry.target.createObserver();
        observer.unobserve(entry.target);
      }
    });
  }
});

class UseAnimate extends HTMLElement {
  constructor() {
    super();
    if(!window.appearAnimate) return;
    this.inited = false;
  }

  connectedCallback() {
    if(window.innerWidth >= 750) {
      this.createObserver();
    } else {
      animateResizeObserver.observe(this);
    }
  }

  disconnectedCallback() {
    if(this.stop) {
      this.stop();
    }
    if(this.inited) {
      animateResizeObserver.unobserve(this);
    }
  }

  createObserver() {
    this.inited = true;
    this.stop = Motion.inView(this, (info) => {
      this.showImages();
    });
  }

  showImages() {
    this.classList.add('loaded');
  }
}

customElements.define('use-animate', UseAnimate);

class ToastMessageManager extends HTMLElement {
  constructor() {
    super();
  }

  pushMessage(content, type) {
    var toast = document.createElement('toast-message');
    toast.setContent(content, type);
    this.appendChild(toast);
    setTimeout(() => {
      toast.classList.add('open');
    }, 300);
  }
}

customElements.define('toast-message-manager', ToastMessageManager);

class ToastMessage extends HTMLElement
{
  constructor() {
    super();
    this.waitToCloseTimeout = setTimeout(() => {
      this.close();
    }, 3300);
  }

  connectedCallback() {
    this.appendCloseButton();
  }

  setContent(str, type) {
    this.appendChild(document.createElement('div'));
    this.contentWrapper = this.querySelector('div');
    this.contentWrapper.classList.add('alert');
    this.contentWrapper.textContent = str;
    this.contentWrapper.classList.add(`alert--${type}`);
  }
 
  close() {
    this.classList.remove('open');
    setTimeout(() => {
      this.remove();
    }, 300);
  }

  appendCloseButton() {
    const button = document.createElement('button');
    button.setAttribute('type', 'button');
    button.classList.add('toast__button', 'color-foreground');
    const icon = document.querySelector('.icon-close-sample').cloneNode(true);
    icon.classList.remove('hidden');
    button.appendChild(icon);
    this.onButtonClickHandler = this.onButtonClick.bind(this);
    button.addEventListener('click', this.onButtonClickHandler);
    this.contentWrapper.appendChild(button);
  }

  onButtonClick() {
    if(this.waitToCloseTimeout) {
      clearTimeout(this.waitToCloseTimeout);
    }
    this.close();
  }
}

customElements.define('toast-message', ToastMessage);

function pushMessage(content, type) {
  document.querySelector('toast-message-manager').pushMessage(content, type);
}

function pushSuccessMessage(content) {
  pushMessage(content, 'success');
}

function pushErrorMessage(content) {
  pushMessage(content, 'error');
}

function findShopifyChatHost() {
  const selectors = ['#ShopifyChat', 'inbox-online-store-chat', 'shopify-chat', 'shopify-agent'];
  for (const selector of selectors) {
    const host = document.querySelector(selector);
    if (host) return host;
  }
  return null;
}

function openShopifyChat(event) {
  if (event) event.preventDefault();

  const chatHost = findShopifyChatHost();
  if (chatHost && typeof chatHost.show === 'function') {
    try {
      chatHost.show();
      return;
    } catch (error) {}
  }

  const shadowRoot = chatHost?.shadowRoot;
  if (shadowRoot) {
    const toggleButton = shadowRoot.querySelector('button.chat-toggle')
      || shadowRoot.querySelector('button.activator')
      || shadowRoot.querySelector('.chat-app > button')
      || shadowRoot.querySelector('button');

    if (toggleButton) {
      toggleButton.click();
      return;
    }
  }

  document.getElementById('shopify-chat')?.classList.add('show');
}

document.addEventListener('click', (event) => {
  const link = event.target.closest('a[href="#chat"], a[href$="#chat"]');
  if (!link) return;
  openShopifyChat(event);
});

(function () {
  const INSTRUCTIONS_CLASS = 'shopify-account-instructions';
  const FALLBACK_CLASS = 'shopify-account-instructions-fallback';
  const MENU_STYLE_ID = 'theme-account-menu-styles';
  const injectedNodes = new WeakMap();
  const fallbackNodes = new WeakMap();
  const positionHandlers = new WeakMap();

  const MENU_ICONS = {
    home: '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M3 12L5 10M5 10L12 3L19 10M5 10V20C5 20.5523 5.44772 21 6 21H9M19 10L21 12M19 10V20C19 20.5523 18.5523 21 18 21H15M9 21C9.55228 21 10 20.5523 10 20V16C10 15.4477 10.4477 15 11 15H13C13.5523 15 14 15.4477 14 16V20C14 20.5523 14.4477 21 15 21M9 21H15" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    account: '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><rect x="7" y="2" width="10" height="10" rx="5" stroke="currentColor" stroke-width="2"/><path d="M12 15.5C13.5 15.5 16 15.8333 16.5 16C17 16.1667 20.2 16.8 21 18C22 19.5 22 20 22 22" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><path d="M12 15.5C10.5 15.5 8 15.8333 7.5 16C7 16.1667 3.8 16.8 3 18C2 19.5 2 20 2 22" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>',
    orders: '<svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><path stroke-linecap="round" stroke="currentColor" stroke-width="1.5" d="M2.929 7.705c0-.482.154-.95.44-1.338L5.06 4.08c.303-.41.783-.652 1.293-.652h7.42c.525 0 1.018.257 1.319.69l1.577 2.266c.262.377.403.826.403 1.285v8.294c0 .888-.72 1.608-1.608 1.608H4.536c-.888 0-1.607-.72-1.607-1.608V7.705Z"/><path stroke="currentColor" stroke-width="1.5" d="M2.929 6.965h14.143M10 3.428v3.535M6.143 14.035v-2.571c0-.178.143-.321.321-.321h3.857c.178 0 .322.143.322.321v2.571a.321.321 0 0 1-.322.322H6.464a.321.321 0 0 1-.321-.322Z"/></svg>',
    logout: '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M11 16L7 12M7 12L11 8M7 12L21 12M16 16V17C16 18.6569 14.6569 20 13 20H6C4.34315 20 3 18.6569 3 17V7C3 5.34315 4.34315 4 6 4H13C14.6569 4 16 5.34315 16 7V8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
  };

  function createInstructionsElement(text) {
    const element = document.createElement('p');
    element.className = INSTRUCTIONS_CLASS;
    element.setAttribute('data-theme-injected', 'true');
    element.textContent = text;
    element.style.margin = '0.8rem 0 1.2rem';
    element.style.fontSize = '1.4rem';
    element.style.lineHeight = '1.5';
    element.style.opacity = '0.85';
    return element;
  }

  function findDialog(shadowRoot) {
    return (
      shadowRoot.querySelector('[role="dialog"]')
      || shadowRoot.querySelector('dialog')
      || shadowRoot.querySelector('[class*="sheet"]')
      || shadowRoot.querySelector('[class*="dialog"]')
      || shadowRoot.querySelector('[class*="panel"]')
    );
  }

  function findHeading(dialog) {
    return dialog.querySelector('h1, h2, h3, [class*="heading"], [class*="title"]');
  }

  function getDialogSurface(shadowRoot) {
    const dialog = findDialog(shadowRoot);
    if (!dialog) return null;

    let surface = dialog;
    let node = dialog.parentElement;

    while (node && node !== shadowRoot) {
      if (node.matches('[class*="sheet"], [class*="popover"], [class*="panel"], [class*="dropdown"], [role="presentation"]')) {
        surface = node;
      }
      node = node.parentElement;
    }

    return surface;
  }

  function positionAccountDialog(host) {
    const shadowRoot = host.shadowRoot;
    if (!shadowRoot) return;

    const surface = getDialogSurface(shadowRoot);
    if (!surface) return;

    const hostRect = host.getBoundingClientRect();
    if (hostRect.width === 0 && hostRect.height === 0) return;

    const gap = 8;
    const edgePadding = 16;
    const dialogWidth = Math.min(360, window.innerWidth - edgePadding * 2);
    const isRtl = document.documentElement.dir === 'rtl';
    const top = Math.round(hostRect.bottom + gap);

    surface.classList.add('theme-account-dialog-positioned');
    surface.setAttribute('data-theme-positioned', 'true');
    surface.style.setProperty('position', 'fixed', 'important');
    surface.style.setProperty('top', `${top}px`, 'important');
    surface.style.setProperty('bottom', 'auto', 'important');
    surface.style.setProperty('margin', '0', 'important');
    surface.style.setProperty('transform', 'none', 'important');
    surface.style.setProperty('width', `${dialogWidth}px`, 'important');
    surface.style.setProperty('max-width', `min(36rem, calc(100vw - ${edgePadding * 2}px))`, 'important');
    surface.style.setProperty('z-index', '9999', 'important');
    surface.style.setProperty('inset-inline-start', 'auto', 'important');
    surface.style.setProperty('inset-inline-end', 'auto', 'important');

    if (isRtl) {
      let left = Math.max(edgePadding, Math.round(hostRect.left));
      if (left + dialogWidth > window.innerWidth - edgePadding) {
        left = Math.max(edgePadding, window.innerWidth - edgePadding - dialogWidth);
      }
      surface.style.setProperty('left', `${left}px`, 'important');
      surface.style.setProperty('right', 'auto', 'important');
      return;
    }

    let right = Math.max(edgePadding, Math.round(window.innerWidth - hostRect.right));
    if (window.innerWidth - right - dialogWidth < edgePadding) {
      right = edgePadding;
    }
    surface.style.setProperty('right', `${right}px`, 'important');
    surface.style.setProperty('left', 'auto', 'important');
  }

  function removeInstructions(host) {
    const injected = injectedNodes.get(host);
    if (injected?.isConnected) injected.remove();
    injectedNodes.delete(host);

    const fallback = fallbackNodes.get(host);
    if (fallback?.isConnected) fallback.remove();
    fallbackNodes.delete(host);
  }

  function escapeHtml(text) {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function positionFallback(host, fallback) {
    const shadowRoot = host.shadowRoot;
    const dialog = shadowRoot ? findDialog(shadowRoot) : null;
    const heading = dialog ? findHeading(dialog) : null;
    const headingRect = heading?.getBoundingClientRect();
    const dialogRect = dialog?.getBoundingClientRect();
    const hostRect = host.getBoundingClientRect();

    if (headingRect && headingRect.height > 0 && dialogRect) {
      fallback.style.top = `${headingRect.bottom + 4}px`;
      fallback.style.left = `${dialogRect.left}px`;
      fallback.style.width = `${dialogRect.width}px`;
      return;
    }

    if (dialogRect && dialogRect.width > 0 && dialogRect.height > 0) {
      fallback.style.top = `${Math.max(0, dialogRect.top + 8)}px`;
      fallback.style.left = `${dialogRect.left}px`;
      fallback.style.width = `${dialogRect.width}px`;
      return;
    }

    fallback.style.top = `${Math.max(16, hostRect.bottom + 8)}px`;
    fallback.style.right = `${Math.max(16, window.innerWidth - hostRect.right)}px`;
    fallback.style.width = 'min(36rem, calc(100vw - 3rem))';
  }

  function injectFallback(host, text) {
    removeInstructions(host);

    const fallback = document.createElement('div');
    fallback.className = FALLBACK_CLASS;
    fallback.setAttribute('data-theme-injected', 'true');
    fallback.innerHTML = `<p class="${INSTRUCTIONS_CLASS}">${escapeHtml(text)}</p>`;
    document.body.appendChild(fallback);
    fallbackNodes.set(host, fallback);
    positionFallback(host, fallback);
  }

  function isAccountSignInView(host) {
    if (host.dataset.customerGreeting) return false;
    if (host.dataset.signInInstructions !== undefined) return true;

    const shadowRoot = host.shadowRoot;
    if (!shadowRoot) return true;

    const dialog = findDialog(shadowRoot);
    if (!dialog) return true;

    if (dialog.querySelector('input[type="email"], input[autocomplete="email"]')) {
      return true;
    }

    const heading = findHeading(dialog);
    if (heading && /sign[\s-]*in|log[\s-]*in|create\s*account/i.test(heading.textContent)) {
      return true;
    }

    const menuLinks = getAccountMenuLinks(dialog);
    const hasSignedInMenu = menuLinks.some((link) => {
      const href = (link.getAttribute('href') || '').toLowerCase();
      return href.includes('/account/orders') || href.includes('/account/profile') || href.includes('/account/addresses');
    });

    if (hasSignedInMenu) return false;

    return true;
  }

  function getLinkLabel(link) {
    return (link.querySelector('.theme-account-menu-item__label')?.textContent || link.textContent || '').trim();
  }

  function isLogoutLink(link) {
    const text = getLinkLabel(link).toLowerCase();
    const href = (link.getAttribute('href') || '').toLowerCase();
    return /log\s*out|sign\s*out/.test(text) || href.includes('logout');
  }

  function isAuthenticatedOnlyLink(link) {
    if (isLogoutLink(link)) return true;

    const text = getLinkLabel(link).toLowerCase();
    const href = (link.getAttribute('href') || '').toLowerCase();
    return href.includes('/account')
      || href.includes('/orders')
      || /account|orders|profile|details/.test(text);
  }

  function findLoggedInGreeting(dialog) {
    const candidates = dialog.querySelectorAll('h1, h2, h3, p, span, div, [class*="heading"], [class*="title"]');

    for (const element of candidates) {
      const text = element.textContent.trim();
      if (/^hi[, ]\s*\S/i.test(text) && text.length < 80 && element.querySelector('a, button, input') === null) {
        return element;
      }
    }

    return null;
  }

  function isAccountMenuAction(element) {
    if (!(element instanceof HTMLAnchorElement)) return false;

    const href = (element.getAttribute('href') || '').trim().toLowerCase();
    const text = getLinkLabel(element).toLowerCase();

    if (!href || href === '#' || href.startsWith('javascript:')) return false;
    if (element.closest('[class*="social"], [class*="oauth"], [class*="provider"]')) return false;
    if (element.closest('[aria-label*="close" i], [class*="close"], button[type="submit"]')) return false;

    return href.includes('/account')
      || href.includes('/orders')
      || href.includes('/profile')
      || /^(orders|profile|account home|my details|log\s*out|sign\s*out)$/i.test(text)
      || /order|profile|detail|account|log\s*out|sign\s*out|home/i.test(text);
  }

  function getAccountMenuLinks(dialog) {
    const signInRoot = dialog.querySelector('input[type="email"], input[autocomplete="email"]')?.closest('form, section, div');

    return Array.from(dialog.querySelectorAll('a[href]')).filter((link) => {
      if (signInRoot?.contains(link)) return false;
      return isAccountMenuAction(link);
    });
  }

  function restructureMenuLayout(links) {
    const parents = new Set();

    links.forEach((link) => {
      const parent = link.parentElement;
      if (parent) parents.add(parent);
    });

    parents.forEach((parent) => {
      parent.classList.add('theme-account-menu-actions');
      parent.closest('ul, ol, nav, [role="menu"]')?.classList.add('theme-account-menu-actions');
    });
  }

  function styleLoggedInHeader(dialog) {
    const greeting = findLoggedInGreeting(dialog);
    if (greeting) greeting.classList.add('theme-account-greeting');

    dialog.querySelectorAll('p, span, div').forEach((element) => {
      if (element.children.length > 0) return;
      if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(element.textContent.trim())) {
        element.classList.add('theme-account-email');
      }
    });
  }

  function getIconForLink(link) {
    const text = getLinkLabel(link).toLowerCase();
    const href = (link.getAttribute('href') || '').toLowerCase();

    if (isLogoutLink(link)) return MENU_ICONS.logout;
    if (/order/.test(text) || href.includes('/orders')) return MENU_ICONS.orders;
    if (/detail|profile/.test(text) || href.includes('/profile')) return MENU_ICONS.account;
    return MENU_ICONS.home;
  }

  function ensureMenuStyles(shadowRoot) {
    if (shadowRoot.querySelector(`#${MENU_STYLE_ID}`)) return;

    const style = document.createElement('style');
    style.id = MENU_STYLE_ID;
    style.textContent = `
      .theme-account-greeting {
        font-family: var(--shopify-account-font-heading, inherit);
        font-weight: var(--shopify-account-font-heading-weight, 600);
        font-size: 1.4rem;
        line-height: 1.4;
        margin: 0 0 0.4rem;
        padding: 0 1.6rem;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        color: var(--shopify-account-color-text, inherit);
      }
      .theme-account-email {
        display: block;
        margin: 0 0 1.6rem;
        padding: 0 1.6rem;
        font-size: 1.2rem;
        line-height: 1.4;
        opacity: 0.65;
        color: var(--shopify-account-color-text, inherit);
      }
      .theme-account-menu-actions {
        display: flex !important;
        flex-direction: column !important;
        align-items: stretch !important;
        gap: 0 !important;
        width: 100% !important;
        padding: 0 !important;
        margin: 0 !important;
        list-style: none !important;
      }
      .theme-account-menu-actions > li,
      .theme-account-menu-actions > a {
        width: 100% !important;
      }
      .theme-account-menu-item {
        display: flex !important;
        align-items: center;
        gap: 1.2rem;
        width: 100%;
        padding: 1.2rem 1.6rem !important;
        margin: 0 !important;
        border: 0 !important;
        border-radius: 0 !important;
        background: transparent !important;
        box-shadow: none !important;
        text-decoration: none;
        color: inherit !important;
        justify-content: flex-start !important;
        font: inherit;
        cursor: pointer;
      }
      .theme-account-menu-item:hover,
      .theme-account-menu-item:focus-visible {
        background: color-mix(in srgb, var(--shopify-account-color-text, #fff) 8%, transparent) !important;
      }
      .theme-account-menu-item__icon {
        display: inline-flex;
        width: 2rem;
        height: 2rem;
        flex-shrink: 0;
        color: inherit;
      }
      .theme-account-menu-item__icon svg {
        width: 100%;
        height: 100%;
      }
      .theme-account-menu-item__label {
        font-size: 1.4rem;
        line-height: 1.4;
      }
      [data-theme-menu-hidden="true"] {
        display: none !important;
      }
      .theme-account-menu-divider {
        margin: 0.4rem 1.6rem 0.8rem;
        border: 0;
        border-top: 1px solid color-mix(in srgb, var(--shopify-account-color-text, #fff) 18%, transparent);
      }
      .theme-account-dialog-positioned {
        inset: auto !important;
        margin: 0 !important;
        transform: none !important;
      }
    `;
    shadowRoot.appendChild(style);
  }

  function enhanceMenuLink(link, iconMarkup) {
    const labelText = getLinkLabel(link);

    if (link.dataset.themeMenuEnhanced !== 'true') {
      link.dataset.themeMenuEnhanced = 'true';
      link.classList.add('theme-account-menu-item');

      const icon = document.createElement('span');
      icon.className = 'theme-account-menu-item__icon';
      icon.innerHTML = iconMarkup;

      const label = document.createElement('span');
      label.className = 'theme-account-menu-item__label';
      label.textContent = labelText;

      link.replaceChildren(icon, label);
      return;
    }

    const icon = link.querySelector('.theme-account-menu-item__icon');
    if (icon) icon.innerHTML = iconMarkup;
    const label = link.querySelector('.theme-account-menu-item__label');
    if (label) label.textContent = labelText;
  }

  function addDividerBefore(listItem) {
    const previous = listItem.previousElementSibling;
    if (previous?.classList?.contains('theme-account-menu-divider')) return;

    const divider = document.createElement('hr');
    divider.className = 'theme-account-menu-divider';
    divider.setAttribute('data-theme-injected', 'true');
    listItem.insertAdjacentElement('beforebegin', divider);
  }

  function setMenuItemHidden(link, hidden) {
    const listItem = link.closest('li') || link;

    if (hidden) {
      listItem.setAttribute('data-theme-menu-hidden', 'true');
      return;
    }

    listItem.removeAttribute('data-theme-menu-hidden');
  }

  function findMenuContainer(dialog, links) {
    if (links.length) {
      const parent = links[0].parentElement;
      if (parent) {
        parent.classList.add('theme-account-menu-actions');
        return parent;
      }
    }

    return dialog.querySelector('.theme-account-menu-actions');
  }

  function ensureLogoutLink(dialog, host, links, logoutUrl) {
    if (links.some(isLogoutLink) || dialog.querySelector('[data-theme-injected="logout"]')) return;

    const logoutLabel = host.dataset.logoutLabel?.trim() || 'Log out';
    const menuContainer = findMenuContainer(dialog, links);

    const divider = document.createElement('hr');
    divider.className = 'theme-account-menu-divider';
    divider.setAttribute('data-theme-injected', 'true');

    const logoutLink = document.createElement('a');
    logoutLink.href = logoutUrl;
    logoutLink.setAttribute('data-no-instant', '');
    logoutLink.setAttribute('data-theme-injected', 'logout');
    logoutLink.dataset.themeMenuEnhanced = 'true';
    logoutLink.className = 'theme-account-menu-item';
    logoutLink.innerHTML = `<span class="theme-account-menu-item__icon">${MENU_ICONS.logout}</span><span class="theme-account-menu-item__label">${escapeHtml(logoutLabel)}</span>`;

    if (menuContainer) {
      menuContainer.appendChild(divider);
      menuContainer.appendChild(logoutLink);
      return;
    }

    const wrapper = document.createElement('div');
    wrapper.className = 'theme-account-menu-actions';
    wrapper.setAttribute('data-theme-injected', 'true');
    wrapper.appendChild(divider);
    wrapper.appendChild(logoutLink);
    dialog.appendChild(wrapper);
  }

  function ensureGreeting(dialog, host) {
    const signedOut = isAccountSignInView(host);
    const injectedGreeting = dialog.querySelector('.theme-account-greeting[data-theme-injected="true"]');

    if (signedOut) {
      injectedGreeting?.remove();
      return;
    }

    const existingGreeting = findLoggedInGreeting(dialog);
    if (existingGreeting) {
      existingGreeting.classList.add('theme-account-greeting');
      return;
    }

    const greeting = host.dataset.customerGreeting?.trim();
    if (!greeting) return;

    const menuRoot = getAccountMenuLinks(dialog)[0]?.closest('ul, nav, [role="menu"], [class*="menu"], .theme-account-menu-actions');
    let greetingElement = dialog.querySelector('.theme-account-greeting[data-theme-injected="true"]');

    if (!greetingElement) {
      greetingElement = document.createElement('p');
      greetingElement.className = 'theme-account-greeting';
      greetingElement.setAttribute('data-theme-injected', 'true');

      if (menuRoot) {
        menuRoot.insertAdjacentElement('beforebegin', greetingElement);
      } else {
        dialog.insertBefore(greetingElement, dialog.firstChild);
      }
    }

    greetingElement.textContent = greeting;
  }

  function customizeAccountMenu(host) {
    try {
      const shadowRoot = host.shadowRoot;
      if (!shadowRoot) return;

      const dialog = findDialog(shadowRoot);
      if (!dialog) return;

      ensureMenuStyles(shadowRoot);

      const signedOut = isAccountSignInView(host);
      const logoutUrl = host.dataset.logoutUrl || '/account/logout';
      const links = getAccountMenuLinks(dialog);

      if (!signedOut) {
        styleLoggedInHeader(dialog);
        restructureMenuLayout(links);
      }

      links.forEach((link) => {
        const logout = isLogoutLink(link);
        const authOnly = isAuthenticatedOnlyLink(link);

        if (signedOut && authOnly) {
          setMenuItemHidden(link, true);
          return;
        }

        setMenuItemHidden(link, false);

        if (logout) {
          link.setAttribute('href', logoutUrl);
          link.setAttribute('data-no-instant', '');
          enhanceMenuLink(link, MENU_ICONS.logout);
          addDividerBefore(link.closest('li') || link);
          return;
        }

        enhanceMenuLink(link, getIconForLink(link));
      });

      if (!signedOut) {
        ensureLogoutLink(dialog, host, links, logoutUrl);
      }

      ensureGreeting(dialog, host);
    } catch (error) {
      console.warn('Account menu customization failed:', error);
    }
  }

  function runAccountMenuTasks(host) {
    try {
      injectInstructions(host);
    } catch (error) {
      console.warn('Account sign-in instructions failed:', error);
    }

    try {
      customizeAccountMenu(host);
    } catch (error) {
      console.warn('Account menu customization failed:', error);
    }

    try {
      positionAccountDialog(host);
    } catch (error) {
      console.warn('Account menu positioning failed:', error);
    }
  }

  function scheduleAccountMenuTasks(host) {
    runAccountMenuTasks(host);
    window.setTimeout(() => runAccountMenuTasks(host), 120);
    window.setTimeout(() => runAccountMenuTasks(host), 320);
  }

  function bindAccountDialogPosition(host) {
    const existingHandler = positionHandlers.get(host);
    if (existingHandler) {
      window.removeEventListener('resize', existingHandler);
      window.removeEventListener('scroll', existingHandler, true);
    }

    const handler = () => positionAccountDialog(host);
    positionHandlers.set(host, handler);
    positionAccountDialog(host);
    window.addEventListener('resize', handler);
    window.addEventListener('scroll', handler, true);
  }

  function unbindAccountDialogPosition(host) {
    const handler = positionHandlers.get(host);
    if (!handler) return;

    window.removeEventListener('resize', handler);
    window.removeEventListener('scroll', handler, true);
    positionHandlers.delete(host);
  }

  function injectInstructions(host) {
    const text = host.dataset.signInInstructions?.trim();
    if (!text) return;

    if (!isAccountSignInView(host)) {
      removeInstructions(host);
      return;
    }

    removeInstructions(host);

    const shadowRoot = host.shadowRoot;
    if (!shadowRoot) {
      injectFallback(host, text);
      return;
    }

    const dialog = findDialog(shadowRoot);
    if (!dialog) {
      injectFallback(host, text);
      return;
    }

    const instructions = createInstructionsElement(text);
    const heading = findHeading(dialog);

    if (heading) {
      heading.insertAdjacentElement('afterend', instructions);
    } else {
      dialog.insertBefore(instructions, dialog.firstChild);
    }

    injectedNodes.set(host, instructions);
  }

  function bindHost(host) {
    if (host.dataset.instructionsBound === 'true') return;
    host.dataset.instructionsBound = 'true';

    host.addEventListener('open', () => {
      bindAccountDialogPosition(host);
      scheduleAccountMenuTasks(host);
    });

    host.addEventListener('close', () => {
      unbindAccountDialogPosition(host);
      removeInstructions(host);
    });
  }

  function scanForHosts() {
    document.querySelectorAll('shopify-account').forEach(bindHost);
  }

  window.addEventListener('resize', () => {
    document.querySelectorAll('shopify-account[data-sign-in-instructions]').forEach((host) => {
      const fallback = fallbackNodes.get(host);
      if (fallback) positionFallback(host, fallback);
    });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scanForHosts);
  } else {
    scanForHosts();
  }

  const observer = new MutationObserver(scanForHosts);
  observer.observe(document.documentElement, { childList: true, subtree: true });
})();